import { formatDating, datingSearchUrl } from '../utils/dating';
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Edit3 } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { fetchProductById, fetchProductChildren } from '../api';
import CustomDescription from '../components/CustomDescription';
import LinkedText from '../components/LinkedText';
import RecordPhotoGrid from '../components/RecordPhotoGrid';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const copy = {
  RU: { back: 'Каталог', kind: 'ЭКЗЕМПЛЯР', untitled: 'Без названия', id: 'ID изделия', number: 'Номер', year: 'Год выпуска', category: 'Назначение', maker: 'Производитель', partOf: 'Является частью', description: 'Описание', source: 'Источник', children: 'Составные изделия', child: 'Изделие', parts: 'Связанные детали', showParts: 'Показать связанные детали', hideParts: 'Скрыть связанные детали', photographs: 'Фотографии', noPhotos: 'Фотографий нет.', openPhoto: 'Открыть', photoInfo: 'Информация о фото', edit: 'Редактировать запись', loading: 'Загрузка записи…', error: 'Не удалось загрузить запись.', noYear: 'год не указан' },
  EN: { back: 'Catalogue', kind: 'EXAMPLE', untitled: 'Untitled', id: 'Record ID', number: 'Serial number', year: 'Production year', category: 'Category', maker: 'Maker', partOf: 'Part of', description: 'Description', source: 'Source', children: 'Component examples', child: 'Example', parts: 'Linked parts', showParts: 'Show linked parts', hideParts: 'Hide linked parts', photographs: 'Photographs', noPhotos: 'No photographs.', openPhoto: 'Open', photoInfo: 'Photo information', edit: 'Edit record', loading: 'Loading record…', error: 'Unable to load the record.', noYear: 'year unknown' },
};

function searchUrl(key, value) {
  const filterable = ['year', 'belongsTo', 'manufacturer'].includes(key);
  return '/search?' + new URLSearchParams(filterable
    ? { searchType: 'all', [key]: String(value) }
    : { searchType: 'all', q: String(value) }).toString();
}

export default function ProductPage() {
  const { id } = useParams();
  const { state } = useLocation();
  const catalogReturnTo = state?.catalogReturnTo || '/search';
  const catalogReturnKey = state?.catalogReturnKey;
  const { currentUser } = useAuth();
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const [product, setProduct] = useState(null);
  useDocumentTitle(product?.name);
  const [children, setChildren] = useState([]);
  const [status, setStatus] = useState('loading');
  const [partsOpen, setPartsOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const cached = sessionStorage.getItem('product:' + id);
    if (cached) {
      try { setProduct(JSON.parse(cached)); } catch {}
    }
    setStatus('loading');
    Promise.all([fetchProductById(id), fetchProductChildren(id).catch(() => ({ data: [] }))])
      .then(([record, childRecords]) => {
        if (!active) return;
        setProduct(record.data);
        setChildren(Array.isArray(childRecords.data) ? childRecords.data : []);
        sessionStorage.setItem('product:' + id, JSON.stringify(record.data));
        setStatus('ready');
      })
      .catch(() => active && setStatus('error'));
    return () => { active = false; };
  }, [id]);

  if (status === 'error' && !product) return <main className="record-state page-width">{labels.error}</main>;
  if (!product) return <main className="record-state page-width">{labels.loading}</main>;

  const facts = [
    product.number && [labels.number, <Link to={searchUrl('q', product.number)}>{product.number}</Link>],
    formatDating(product, lang) && [labels.year, <Link to={datingSearchUrl(product)}>{formatDating(product, lang)}</Link>],
    product.belongsTo && [labels.category, <Link to={searchUrl('belongsTo', product.belongsTo)}>{product.belongsTo}</Link>],
    product.manufacturer && [labels.maker, <Link to={'/manufacturers/' + product.manufacturer.id}>{product.manufacturer.name}</Link>],
    product.parent?.id && [labels.partOf, <Link to={'/products/' + product.parent.id} state={{ catalogReturnTo, catalogReturnKey }}>{product.parent.name || '#' + product.parent.id}</Link>],
  ].filter(Boolean);

  return (
    <main className="record-page page-width">
      <header className="record-header">
        <div className="record-breadcrumb"><Link to={catalogReturnTo} state={{ restoreScrollKey: catalogReturnKey }}>← {labels.back}</Link><span>/</span><span>{labels.kind} {String(product.id).padStart(5, '0')}</span></div>
        <div className="record-title-row">
          <div><p className="section-kicker">{labels.kind}</p><h1>{product.name || labels.untitled}</h1><small>{labels.id}: {product.id}</small></div>
          {currentUser && <Link to={'/products/' + product.id + '/edit'} className="secondary-button"><Edit3 size={16} />{labels.edit}</Link>}
        </div>
      </header>

      <div className="record-layout">
        <div className="record-main">
          {facts.length > 0 && <dl className="record-fact-sheet">{facts.map(([term, value]) => <React.Fragment key={term}><dt>{term}</dt><dd>{value}</dd></React.Fragment>)}</dl>}

          {product.description && <section className="record-section"><h2>{labels.description}</h2><CustomDescription html={product.description} /></section>}
          {product.source && <section className="record-section"><h2>{labels.source}</h2><div className="record-source"><LinkedText>{product.source}</LinkedText></div></section>}

          {children.length > 0 && (
            <section className="record-section">
              <h2>{labels.children}<span>{children.length}</span></h2>
              <div className="linked-records">
                {children.map((child) => <Link to={'/products/' + child.id} state={{ catalogReturnTo, catalogReturnKey }} key={child.id}><span>{labels.child} {String(child.id).padStart(5, '0')}</span><strong>{child.name || '#' + child.id}</strong><small>{[formatDating(child, lang) || labels.noYear, child.number && '№ ' + child.number, child.manufacturer?.name].filter(Boolean).join(' · ')}</small></Link>)}
              </div>
            </section>
          )}

          {product.details?.length > 0 && (
            <section className="record-section">
              <button type="button" className="record-disclosure" onClick={() => setPartsOpen((value) => !value)} aria-expanded={partsOpen}>
                <span>{partsOpen ? labels.hideParts : labels.showParts}<small>{product.details.length}</small></span>{partsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {partsOpen && <div className="linked-records linked-records--parts">{product.details.map((detail) => <Link to={'/details/' + detail.id} state={{ catalogReturnTo, catalogReturnKey }} key={detail.id}><span>{detail.type?.name || labels.parts}</span><strong>{detail.name || (detail.number ? '№ ' + detail.number : '#' + detail.id)}</strong><small>{[formatDating(detail, lang), detail.number && '№ ' + detail.number].filter(Boolean).join(' · ')}</small></Link>)}</div>}
            </section>
          )}
        </div>

        <aside className="record-aside"><h2>{labels.photographs}<span>{product.photos?.length || 0}</span></h2><RecordPhotoGrid photos={product.photos || []} labels={{ empty: labels.noPhotos, open: labels.openPhoto, info: labels.photoInfo }} /></aside>
      </div>
    </main>
  );
}
